import React, { useState } from "react"
import Box from "@mui/material/Box"
import Typography from "@mui/material/Typography"
import PlayArrowIcon from "@mui/icons-material/PlayArrow"
import { HOW_TO_USE } from "@/features/landing/content"

const getYouTubeVideoId = (src: string): string | null => {
  const match = src.match(/youtube\.com\/embed\/([^?&]+)/)
  return match?.[1] ?? null
}

const HowToUseContent: React.FC = () => (
  <Box>
    <Typography variant="body1" sx={{ color: "text.secondary", textAlign: "center", mb: 4 }}>
      {HOW_TO_USE.body}
    </Typography>
    <HowToUsePlayer />
  </Box>
)

export const HowToUsePlayer: React.FC = () => {
  const { videoSrc, heading } = HOW_TO_USE
  const [playing, setPlaying] = useState(false)
  const videoId = getYouTubeVideoId(videoSrc)
  const thumbnailUrl = videoId ? `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg` : null
  const autoplaySrc = `${videoSrc}${videoSrc.includes("?") ? "&" : "?"}autoplay=1`

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: 800,
        mx: "auto",
        aspectRatio: "16/9",
        borderRadius: 2,
        overflow: "hidden",
        boxShadow: 4,
        position: "relative",
        bgcolor: "common.black"
      }}>
      {playing ? (
        <iframe
          src={autoplaySrc}
          title={heading}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          referrerPolicy="strict-origin-when-cross-origin"
          style={{ width: "100%", height: "100%", border: "none", display: "block" }}
        />
      ) : (
        <Box
          role="button"
          tabIndex={0}
          aria-label={`Play tutorial video: ${heading}`}
          onClick={() => setPlaying(true)}
          onKeyDown={event => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault()
              setPlaying(true)
            }
          }}
          sx={{ width: "100%", height: "100%", cursor: "pointer", position: "relative" }}>
          {thumbnailUrl && (
            <Box
              component="img"
              src={thumbnailUrl}
              alt={heading}
              sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
            />
          )}
          {!thumbnailUrl && (
            <Typography
              variant="h5"
              component="span"
              sx={{
                position: "absolute",
                insetInline: 3,
                top: "25%",
                color: "common.white",
                textAlign: "center",
                fontWeight: 700
              }}>
              {heading}
            </Typography>
          )}
          {/* Dark overlay */}
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              bgcolor: "rgba(0,0,0,0.25)",
              transition: "background-color 0.2s",
              "&:hover": { bgcolor: "rgba(0,0,0,0.35)" }
            }}
          />
          {/* Play button */}
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}>
            <Box
              sx={{
                width: 72,
                height: 72,
                borderRadius: "50%",
                bgcolor: "primary.main",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 4px 24px rgba(0,0,0,0.4)",
                transition: "transform 0.15s, box-shadow 0.15s",
                "&:hover": {
                  transform: "scale(1.08)",
                  boxShadow: "0 6px 32px rgba(0,0,0,0.5)"
                }
              }}>
              <PlayArrowIcon sx={{ fontSize: 40, color: "common.white", ml: 0.5 }} />
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  )
}

export default HowToUseContent
